import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  VpnKey as VpnKeyIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

interface PPPSecret {
  id: string;
  name: string;
  service: string;
  profile: string;
  disabled: boolean;
  comment?: string;
}

interface PPPSecretSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (secret: PPPSecret) => void;
  onCreateNew?: () => void;
  secrets: PPPSecret[];
  isLoading: boolean;
  error?: string;
}

const PPPSecretSelectionDialog: React.FC<PPPSecretSelectionDialogProps> = ({
  open,
  onClose,
  onSelect,
  onCreateNew,
  secrets,
  isLoading,
  error,
}) => {
  const [selectedSecret, setSelectedSecret] = useState<PPPSecret | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredSecrets = secrets.filter(secret =>
    secret.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    secret.profile.toLowerCase().includes(searchTerm.toLowerCase()) ||
    secret.comment?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = () => {
    if (selectedSecret) {
      onSelect(selectedSecret);
      setSelectedSecret(null);
      setSearchTerm('');
    }
  };

  const handleClose = () => {
    setSelectedSecret(null);
    setSearchTerm('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <VpnKeyIcon color="primary" />
          <Typography variant="h6">Pilih PPP Secret yang Sudah Ada</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box mb={2}>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Ditemukan {secrets.length} PPP Secret yang sudah ada di router. Pilih salah satu untuk menggunakan kredensial yang sudah ada, atau buat secret baru.
          </Typography>

          {/* Search Field */}
          <TextField
            fullWidth
            placeholder="Cari berdasarkan nama, profile, atau comment..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
        </Box>

        {isLoading && (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Memuat PPP Secret dari router...
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!isLoading && !error && filteredSecrets.length === 0 && searchTerm && (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
            Tidak ada PPP Secret yang cocok dengan pencarian "{searchTerm}"
          </Typography>
        )}

        {!isLoading && !error && filteredSecrets.length === 0 && !searchTerm && (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
            Tidak ada PPP Secret ditemukan di router
          </Typography>
        )}

        {!isLoading && !error && filteredSecrets.length > 0 && (
          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {filteredSecrets.map((secret) => (
              <ListItem key={secret.id} disablePadding>
                <ListItemButton
                  selected={selectedSecret?.id === secret.id}
                  onClick={() => setSelectedSecret(secret)}
                  sx={{
                    border: 1,
                    borderColor: selectedSecret?.id === secret.id ? 'primary.main' : 'grey.300',
                    borderRadius: 1,
                    mb: 1,
                    '&.Mui-selected': {
                      backgroundColor: 'primary.50',
                    },
                  }}
                >
                  <ListItemIcon>
                    {selectedSecret?.id === secret.id ? (
                      <CheckCircleIcon color="primary" />
                    ) : (
                      <VpnKeyIcon color="action" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {secret.name}
                        </Typography>
                        {secret.disabled && (
                          <Chip label="Disabled" size="small" color="error" variant="outlined" />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Profile: <strong>{secret.profile}</strong> | Service: <strong>{secret.service}</strong>
                        </Typography>
                        {secret.comment && (
                          <Typography variant="caption" color="text.secondary">
                            Comment: {secret.comment}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}

        {filteredSecrets.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            Menampilkan {filteredSecrets.length} dari {secrets.length} PPP Secret
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Batal
        </Button>
        {onCreateNew && (
          <Button
            onClick={() => {
              setSelectedSecret(null);
              setSearchTerm('');
              onCreateNew();
            }}
            variant="outlined"
            color="primary"
          >
            Buat Secret Baru
          </Button>
        )}
        <Button
          onClick={handleSelect}
          variant="contained"
          disabled={!selectedSecret}
          startIcon={<CheckCircleIcon />}
        >
          Gunakan Secret Ini
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PPPSecretSelectionDialog;
